import DataGrid, {
    Editing, Paging, Column, Pager, Scrolling, FilterRow, HeaderFilter, DataGridTypes, RequiredRule, Lookup,
    MasterDetail, Button, Texts, Toolbar, Item, Sorting, Selection, GroupPanel,
    KeyboardNavigation,
    ColumnChooser,
    SearchPanel,
    Export,
    CustomRule,
    Summary,
    TotalItem,
    EmailRule
} from 'devextreme-react/data-grid';
import React, { useCallback, useState } from 'react';
import { t } from 'i18next';
import { toast } from 'react-toastify';
import * as authHelper from '@modules/auth/core/AuthHelpers'

import { Workbook } from 'exceljs';
import { exportDataGrid } from 'devextreme/excel_exporter';
import saveAs from 'file-saver';

export interface DataGridWrapperProps2 {
    gridRef?: DataGrid | undefined;
    datasource?: any;
    columns: any[];
    sortingMode?: "single" | "multiple" | "none";
    onRowUpdating?: (e: any) => Promise<void>;
    onInitNewRow?: (e: any) => Promise<void>;
    onEditorPreparing?: (e: any) => void;
    onToolbarPreparing?: (e: any) => void;
    onInitialized?: (e: any) => void;
    onSelectionChanged?: (e: any) => void;
    onContentReady?: (e: any) => void;
    onSaving?: (e: any) => void;
    onEditingStart?: (e: any) => void;
    onExporting?: (e: any) => void;
    onFocusedCellChanging?: (e: any) => void;
    onCellPrepared?: (e: any) => void;
    onCellEditingStart?: (e: any) => void;
    customToolbarContent?: any;
    remoteOperations?: boolean;
    pagerVisible?: boolean;
    filterVisible?: boolean;
    searchPanelVisible?: boolean;
    editingConfig?: any;
    headerFilterVisible?: boolean;
    props?: any;
    masterDetail?: any | undefined;
    buttonConfig?: any[] | undefined;
    grouping?: boolean | undefined;
    masterGridRef?: any | undefined;
    selectionConfig?: any | undefined;
    height?: any | undefined;
    keyboardNavigation?: boolean;
    showColumnLines?: boolean;
    columnChooser?: boolean;
    excellExport?: boolean;
    permissonCode?: string | undefined;
    summary?: any[] | undefined;
}
const DataGridWrapper: React.FC<DataGridWrapperProps2> = ({
    gridRef,
    datasource,
    columns,
    pagerVisible,
    filterVisible,
    searchPanelVisible,
    headerFilterVisible,
    masterGridRef,
    customToolbarContent,
    remoteOperations,
    editingConfig,
    props,
    masterDetail = undefined,
    buttonConfig = [],
    sortingMode,
    grouping,
    selectionConfig,
    height,
    permissonCode,
    keyboardNavigation,
    columnChooser,
    showColumnLines,
    excellExport,
    onFocusedCellChanging,
    onSaving,
    onEditingStart,
    onRowUpdating,
    onInitNewRow,
    onEditorPreparing,
    onToolbarPreparing,
    onInitialized,
    onSelectionChanged,
    onContentReady,
    onCellPrepared,
    onCellEditingStart,
    summary
}) => {

    const allowedPageSizes: (DataGridTypes.PagerPageSize | number)[] = [20, 50, 100];


    const onDataErrorOccurred = (e: any) => {
        try {
            toast.error(e.error.response.data.message);
        } catch (error) {
            toast.error(t("failed"));
        }
    }

    const onSaved = (e: any) => {
        if (remoteOperations !== false)
            toast.success(t("saved_changes"));
    }


    const checkPermissions = (action: string): boolean => {
        var permissonResult = true;
        if (permissonCode) {
            permissonResult = authHelper.checkPermission(permissonCode, action);
        }
        return permissonResult;
    }
    const onExporting = (e: DataGridTypes.ExportingEvent) => {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Main sheet');

        exportDataGrid({
            component: e.component,
            worksheet,
            autoFilterEnabled: true,
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
            });
        });
    };

    return (
        <React.Fragment>
            <DataGrid
                ref={gridRef}
                showBorders={true}
                showColumnLines={showColumnLines == undefined ? false : showColumnLines}
                dataSource={datasource}
                repaintChangesOnly={true}
                rowAlternationEnabled={true}
                width="100%"
                onFocusedCellChanging={onFocusedCellChanging}
                onSaving={onSaving}
                onExporting={onExporting}
                onEditingStart={onEditingStart}
                onRowUpdating={onRowUpdating}
                onInitNewRow={onInitNewRow}
                onEditorPreparing={onEditorPreparing}
                onToolbarPreparing={onToolbarPreparing}
                onInitialized={onInitialized}
                onSelectionChanged={onSelectionChanged}
                onDataErrorOccurred={onDataErrorOccurred}
                onContentReady={onContentReady}
                onCellPrepared={onCellPrepared}
                onCellEditingStart={onCellEditingStart}
                onSaved={onSaved}
                errorRowEnabled={false}
                allowColumnResizing={true}
                height={height != undefined ? height : 600}
                remoteOperations={remoteOperations != undefined ? remoteOperations : true}
                {...props}
            >
                {
                    columnChooser &&
                    <ColumnChooser
                        height='340px'
                        enabled={true}
                        mode="select"
                    ></ColumnChooser>
                }

                {sortingMode &&
                    <Sorting mode={sortingMode} />}

                <SearchPanel visible={searchPanelVisible} placeholder={t("search")} />
                <Scrolling mode={"standard"} showScrollbar={'onHover'} />
                <FilterRow visible={filterVisible} />

                {
                    keyboardNavigation &&
                    <KeyboardNavigation
                        editOnKeyPress={true}
                        enterKeyAction="startEdit"
                        enterKeyDirection="column"
                    />
                }

                <HeaderFilter visible={headerFilterVisible}
                    search={{ enabled: true }}
                    allowSelectAll={true} />
                {editingConfig && <Editing
                    refreshMode={editingConfig.refreshMode}
                    mode={editingConfig.mode || 'row'}
                    allowAdding={editingConfig.allowAdding && checkPermissions('Create')}
                    allowDeleting={editingConfig.allowDeleting && checkPermissions('Update')}
                    allowUpdating={editingConfig.allowUpdating && checkPermissions('Delete')}
                    confirmDelete={editingConfig.confirmDelete}
                    useIcons={editingConfig.useIcons}
                    startEditAction="dblClick"
                >
                    <Texts

                        confirmDeleteMessage={editingConfig.deleteMessageText}
                        confirmDeleteTitle={t('warning')}
                    />
                </Editing>}

                {editingConfig == undefined && <Editing
                    refreshMode={"reshape"}
                    mode={'batch'}
                    allowAdding={checkPermissions('Create')}
                    allowDeleting={checkPermissions('Update')}
                    allowUpdating={checkPermissions('Delete')}
                >
                </Editing>}

                <Paging defaultPageSize={20} />
                <Pager
                    showNavigationButtons={true}
                    showInfo={true}
                    allowedPageSizes={allowedPageSizes}
                    showPageSizeSelector={true}
                    visible={pagerVisible}
                />
                {grouping &&
                    <GroupPanel visible={true} />}
                {columns.map((col: any, index: any) => {
                    if (col)
                        return (
                            <Column key={index} {...col} allowEditing={col.allowEditing} >

                                {
                                    col.customRequired
                                    &&
                                    <CustomRule
                                        type="custom"
                                        validationCallback={col.callFunc}
                                        message={col.customErrorMessage}
                                    />
                                }
 
                                { col.emailRule && <EmailRule /> }
 
                                {col.required && <RequiredRule message={col.requiredMessage} />}
                                {col.lookup && (
                                    <Lookup
                                        dataSource={col.lookup.dataSource}
                                        valueExpr={col.lookup.valueExpr}
                                        displayExpr={col.lookup.displayExpr}
                                    />
                                )}
                                {col.type == 'buttons' &&
                                    col.buttons.map((btn: any, index: number) => (
                                        <Button
                                            key={index}
                                            name={btn.name}
                                            hint={btn.hint}
                                            onClick={btn.onClick}
                                        />
                                    ))
                                }
                            </Column>
                        )
                })}

                {buttonConfig.length > 0 && <Column type="buttons">
                    {buttonConfig.map((btn: any, index: number) => (
                        <Button
                            key={index}
                            name={btn.name}
                            hint={btn.hint}
                            onClick={btn.onClick}
                        />
                    ))}
                </Column>}
                {
                    summary != undefined &&
                    <Summary>
                        {
                            summary != undefined &&
                            summary.map((sum: any, index: number) => {
                                return <TotalItem
                                    column={sum.column}
                                    summaryType={sum.summaryType}
                                    valueFormat={{
                                        type: "fixedPoint",
                                        precision: 2
                                    }}
                                >
                                </TotalItem>
                            }
                            )
                        }
                    </Summary>
                }
                {masterDetail && masterGridRef &&
                    <MasterDetail enabled={true} component={masterDetail} ref={masterGridRef} />
                }
                {masterDetail &&
                    <MasterDetail enabled={true} component={masterDetail} />
                }
                {customToolbarContent
                }
                {selectionConfig &&
                    <Selection {...selectionConfig} />
                }
                {excellExport &&
                    <Export enabled={true} allowExportSelectedData={true} />
                }

            </DataGrid>
        </React.Fragment>
    );
};

export default DataGridWrapper;
